/**
 * Extract evenly distributed frames from a video for 360 capture.
 * Uses canvas + video element to capture frames at specific timestamps.
 * Keeps max 1280px for faster extraction and smaller JPEGs.
 */

const JPEG_QUALITY = 0.85;
const MAX_WIDTH = 1280;
const MAX_HEIGHT = 720;

/**
 * Extracts `frameCount` frames from a video, evenly distributed between startTime and endTime.
 * @param videoBlob - The recorded video blob
 * @param startTimeSec - Start time in seconds (trim start)
 * @param endTimeSec - End time in seconds (trim end)
 * @param frameCount - Number of frames to extract (default 72)
 * @param onProgress - Optional callback (done, total) for progress updates
 * @returns Array of Blobs (JPEG) - one per frame
 */
export async function extractFramesFromVideo(
  videoBlob: Blob,
  startTimeSec: number,
  endTimeSec: number,
  frameCount: number = 72,
  onProgress?: (done: number, total: number) => void
): Promise<Blob[]> {
  const start = Math.max(0, startTimeSec);
  const end = Math.max(start + 0.001, endTimeSec);
  const duration = end - start;
  const videoUrl = URL.createObjectURL(videoBlob);

  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = "anonymous";
    video.preload = "auto";

    video.onloadedmetadata = async () => {
      try {
        const frames: Blob[] = [];
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas 2D context not available"));
          return;
        }

        let w = video.videoWidth;
        let h = video.videoHeight;
        if (w > MAX_WIDTH || h > MAX_HEIGHT) {
          const scale = Math.min(MAX_WIDTH / w, MAX_HEIGHT / h);
          w = Math.round(w * scale);
          h = Math.round(h * scale);
        }
        canvas.width = w;
        canvas.height = h;

        for (let i = 0; i < frameCount; i++) {
          const divisor = frameCount > 1 ? frameCount - 1 : 1;
          const t = Math.max(start, Math.min(end, start + (duration * i) / divisor));
          video.currentTime = t;
          await new Promise<void>((res, rej) => {
            video.onseeked = () => {
              ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight, 0, 0, w, h);
              canvas.toBlob(
                (blob) => {
                  if (blob) frames.push(blob);
                  onProgress?.(frames.length, frameCount);
                  res();
                },
                "image/jpeg",
                JPEG_QUALITY
              );
            };
            video.onerror = () => rej(video.error);
          });
        }

        URL.revokeObjectURL(videoUrl);
        resolve(frames);
      } catch (err) {
        URL.revokeObjectURL(videoUrl);
        reject(err);
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(videoUrl);
      reject(new Error("Failed to load video"));
    };

    video.src = videoUrl;
    video.load();
  });
}
